import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isNil } from 'lodash';

import { useEventHub } from '@satellite/composables/useEventHub';
import { matchSocketEventToEntity, SocketEventMatch } from '@satellite/helpers/socketEventMatch';
import appointmentService from '@satellite/services/appointment-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterMode,
  isEntityPresenterQueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterEntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { DebouncedSocketEvent } from '@satellite/helpers/socketEventTypes';
import type { PresenterPlaceholderData } from './presenterPlaceholder';
import type { AppointmentDetails } from './appointmentPresenterShared';
import type { YardAssetVisitDetails } from './yardAssetVisitPresenterShared';

/** `create-`/`update-AssetVisit` payload: the full `assetvisit` row (DB notify trigger). */
type AssetVisitSocketEvent = DebouncedSocketEvent &
  Pick<YardAssetVisitDetails, 'visitType' | 'companyId'> & {
    appointmentId?: Maybe<string>;
  };

interface LinkedAssetVisit {
  id: Maybe<string>;
  visit: Maybe<Pick<YardAssetVisitDetails, 'visitType' | 'companyId'>>;
}

function hasJoinedFieldsChanged(
  event: AssetVisitSocketEvent,
  linkedVisit: LinkedAssetVisit
): boolean {
  return (
    (event.visitType ?? null) !== (linkedVisit.visit?.visitType ?? null) ||
    (event.companyId ?? null) !== (linkedVisit.visit?.companyId ?? null)
  );
}

/**
 * Whether a visit row changes what the appointment's `assetVisit` join returns. The payload
 * is always the genuine row of `event.id`, even in a collapsed burst; only the other rows of
 * the burst are reduced to `debounce.ids`.
 *
 * A new link hidden in the middle of a burst is not chased: this fetcher backs whole lists of
 * appointments without a visit (the link dialog, yard rows), and waking all of them on every
 * yard burst costs far more. That link still updates the appointment row, whose own event
 * triggers a refetch.
 */
function affectsJoinedAssetVisit(
  event: AssetVisitSocketEvent,
  appointmentId: string,
  linkedVisit: LinkedAssetVisit
): boolean {
  switch (matchSocketEventToEntity(event, linkedVisit.id)) {
    /** The linked visit changed in a burst forwarded under another visit's row. */
    case SocketEventMatch.Collapsed:
      return true;
    /** The linked visit itself: unlinked, moved, or a joined field changed. */
    case SocketEventMatch.Direct:
      return event.appointmentId !== appointmentId || hasJoinedFieldsChanged(event, linkedVisit);
    /** Another visit (or none linked yet): only a new link to this appointment matters. */
    default:
      return event.appointmentId === appointmentId;
  }
}

/**
 * Shared CRUD `join` for the appointment GET. Field-limited so payloads stay lean while
 * covering common presenter needs: assetVisit summary, dock/loadType labels (including
 * direction/operation for yard visit-type inference), warehouse access fields + features
 * (YMS gating), nested warehouse.org settings (settings inheritance), carrier company
 * (asset-create prefill), and recurringParent (recurring-pattern header).
 *
 * Nested relations must be their own join path (`dock.warehouse.warehouseFeatures`,
 * `dock.warehouse.org`), not a field on `dock.warehouse||…` — nestjsx/crud `||`
 * selects columns only.
 */
const APPOINTMENT_QUERY_JOINS = [
  'assetVisit||id,visitType,companyId',
  /**
   * `loadTypeIds` lets load type pickers narrow to what this dock actually offers;
   * `capacityParentId` lets a capacity child be labelled after its parent dock.
   */
  'dock||id,name,warehouseId,loadTypeIds,capacityParentId',
  'loadType||id,name,direction,operation',
  /** Address fields back the copyable warehouse address in the reserve dialog. */
  'dock.warehouse||id,orgId,timezone,settings,name,email,phone,street,city,state,zip,allowCarrierScheduling,customApptFieldsTemplate',
  'dock.warehouse.warehouseFeatures||featureCode',
  /**
   * Nested org settings so lean dock.warehouse payloads can consolidate via
   * makeEntitySettings without a separate warehouse GET or FE attach helper.
   * Requires `dock.warehouse.org` on the appointment CRUD join map.
   */
  'dock.warehouse.org||id,settings',
  'user||id,firstName,lastName,email,phone,companyId',
  'user.company||id,name,scac',
  'recurringParent||id,start,recurringPattern'
];

export interface UseAppointmentFetcherOptions {
  /**
   * Appointment ID to fetch. Can be a ref for reactivity.
   * Optional if initialAppointmentData (with id) is provided.
   */
  appointmentId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial appointment data used as initialData for the query.
   */
  initialAppointmentData?:
    | Ref<Maybe<AppointmentDetails>>
    | ComputedRef<Maybe<AppointmentDetails>>
    | AppointmentDetails
    | null;

  /**
   * Optional incomplete appointment data shown as placeholderData while the query fetches.
   */
  placeholderAppointmentData?:
    | Ref<Maybe<PresenterPlaceholderData<AppointmentDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<AppointmentDetails>>>
    | PresenterPlaceholderData<AppointmentDetails>
    | null;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticAppointmentData only
   */
  mode?: EntityPresenterMode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticAppointmentData?:
    | Ref<Maybe<AppointmentDetails>>
    | ComputedRef<Maybe<AppointmentDetails>>
    | AppointmentDetails
    | null;

  staleTime?: number;

  onUpdateReceived?: (appointment: AppointmentDetails) => void;
}

export function useAppointmentFetcher(options: UseAppointmentFetcherOptions) {
  const {
    appointmentId,
    initialAppointmentData,
    placeholderAppointmentData,
    headless = false,
    mode = EntityPresenterMode.Live,
    staticAppointmentData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAppointmentId = computed((): string | null => {
    const explicitId = unref(appointmentId);
    const fromObject = toValue(initialAppointmentData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useAppointmentFetcher] appointmentId mismatch:',
          `explicit appointmentId (${explicitId}) does not match appointment.id (${fromObject}). Using explicit appointmentId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialAppointment = computed(() => toValue(initialAppointmentData) ?? undefined);
  const placeholderAppointment = computed(() => toValue(placeholderAppointmentData) ?? undefined);
  const staticAppointment = computed(() => toValue(staticAppointmentData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (!isNil(toValue(initialAppointmentData)) || !isNil(toValue(placeholderAppointmentData)))
  ) {
    console.warn(
      '[useAppointmentFetcher] mode=static ignores initialAppointmentData/placeholderAppointmentData. Use staticAppointmentData.'
    );
  }

  if (isLazyLiveMode(mode) && !isNil(toValue(initialAppointmentData))) {
    console.warn(
      '[useAppointmentFetcher] mode=lazy-live does not use initialAppointmentData for the query cache; use placeholderAppointmentData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const appointmentQuery = useQuery({
    queryKey: computed(() =>
      effectiveAppointmentId.value ? queryKeys.appointment(effectiveAppointmentId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAppointmentId.value) {
        return null;
      }

      const response = await appointmentService.getAppointmentByIdV2(effectiveAppointmentId.value, {
        requestConfig: {
          signal,
          params: {
            join: APPOINTMENT_QUERY_JOINS
          }
        }
      });

      return response as AppointmentDetails;
    },
    enabled: computed(() =>
      isEntityPresenterQueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveAppointmentId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialAppointment.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderAppointment.value as AppointmentDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): AppointmentDetails | null => {
    if (isStaticMode(mode)) {
      if (!staticAppointment.value) {
        console.warn('[useAppointmentFetcher] mode=static requires staticAppointmentData.');
        return null;
      }
      return staticAppointment.value;
    }
    return appointmentQuery.data.value || null;
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : appointmentQuery.isLoading.value));
  const isFetching = computed(() =>
    isStaticMode(mode) ? false : appointmentQuery.isFetching.value
  );
  const error = computed(() => (isStaticMode(mode) ? null : appointmentQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();

    if (effectiveAppointmentId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.appointment(effectiveAppointmentId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await appointmentQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleAppointmentEvent = (event: DebouncedSocketEvent) => {
    if (matchSocketEventToEntity(event, effectiveAppointmentId.value) === SocketEventMatch.None) {
      return;
    }

    deferOrInvalidate();
  };

  /**
   * Linking or unlinking a check-in writes `assetvisit.appointmentId`, which the
   * `assetVisit||id,visitType,companyId` join reads. The link/unlink endpoints touch the
   * appointment row too, but in a separate statement that commits first, so the refetch
   * that `update-Appointment` triggers can still read the old link. The visit's own event
   * carries the settled value, and covers links made from anywhere, by anyone.
   */
  const handleAssetVisitEvent = (event: AssetVisitSocketEvent) => {
    const appointmentIdValue = effectiveAppointmentId.value;
    if (!event || !appointmentIdValue) {
      return;
    }

    const linkedVisit = {
      id: data.value?.assetVisitId ?? data.value?.assetVisit?.id ?? null,
      visit: data.value?.assetVisit ?? null
    };

    if (affectsJoinedAssetVisit(event, appointmentIdValue, linkedVisit)) {
      deferOrInvalidate();
    }
  };

  const handleWarehouseEvent = (event: DebouncedSocketEvent) => {
    if (matchSocketEventToEntity(event, data.value?.dock?.warehouseId) === SocketEventMatch.None) {
      return;
    }

    deferOrInvalidate();
  };

  watch(data, newAppointment => {
    if (onUpdateReceived && newAppointment) {
      onUpdateReceived(newAppointment);
    }
  });

  watch(effectiveAppointmentId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-Appointment', handleAppointmentEvent);
      /**
       * TODO: When we remove the appointment.customFields we should
       * evaluate if we still need to invalidate the appointment query when the warehouse changes.
       */
      eventHub.on('update-Warehouse', handleWarehouseEvent);
      eventHub.on('create-AssetVisit', handleAssetVisitEvent);
      eventHub.on('update-AssetVisit', handleAssetVisitEvent);
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-Appointment', handleAppointmentEvent);
      eventHub.off('update-Warehouse', handleWarehouseEvent);
      eventHub.off('create-AssetVisit', handleAssetVisitEvent);
      eventHub.off('update-AssetVisit', handleAssetVisitEvent);
    }
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: appointmentQuery
  };
}
